import _ from 'lodash'
import { Observable, from, of } from 'rxjs'
import { catchError,  first, switchMap, tap} from 'rxjs/operators'

import { AuditCampaign, PushCachedValueResult, ProjectLinkableItemStateResource, SynchroMediaUpdates } from '@skinsoft/types'
import { SEARCH_TYPES } from '@skinsoft/const'
import { auditCampaignSelectors } from '@skinsoft/selectors'
import { OfflineDataFetcher, AuditCampaignOfflineDataFetcher } from '@skinsoft/offline'
import { mergeResource } from '@skinsoft/utils'
import { EntityDataStore } from '@skinsoft/entity-data-store'
import { ItemDataStore, RecordDataStore } from '@skinsoft/entity'

const DEFAULT_ITEM_TYPE: string = "physicalMuseumItem"

export class AuditCampaignDataStore extends EntityDataStore<AuditCampaign>{

    public static getInstance() {
        if (!AuditCampaignDataStore.instance) {
            AuditCampaignDataStore.instance = new AuditCampaignDataStore();
        }

        return AuditCampaignDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$auditCampaignDataStore = AuditCampaignDataStore.getInstance();
    }

    private static instance: AuditCampaignDataStore = null as any;

    constructor() {
        super('audit-projects', [SEARCH_TYPES.auditCampaign]);
    }

    public exists(id: AuditCampaign['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise();
    }

    protected pushCachedUpdate(dataToUpdate: SynchroMediaUpdates<AuditCampaign>): Observable<PushCachedValueResult> {
        if (dataToUpdate.id && dataToUpdate.body) {
            const allItems = auditCampaignSelectors.linkableItems(dataToUpdate.body)
                .filter(linkableItem => {
                    return linkableItem.linkableItem?.linkableItem
                })

            const newItemsIds: string[] = allItems?.map(
                item => {
                    const ref = (item.linkableItem?.linkableItem as any).ref ?? item.linkableItem?.linkableItem
                    return ref
                }
            ).filter(
                itemId => {
                    return OfflineDataFetcher.isTemporaryId(itemId)
                }
            )

            const cachedChanges = this.prepareDataToSyncUp(dataToUpdate.body)

            return this.fetchOneOnlineMode(dataToUpdate.id)
                .pipe(
                    switchMap(distantAuditCampaign => {
                        const mergedAuditCampaign = mergeResource(distantAuditCampaign, cachedChanges)

                        return this.update(
                            dataToUpdate.id,
                            mergedAuditCampaign,
                            undefined,
                            true
                        )
                    }),
                    first(),
                    // Handle success or error if the update and emit boolean
                    catchError(e => of(Error('Error during update...'))),
                    switchMap(() => {
                        return this.fetchOneOnlineMode(dataToUpdate.id)
                    }),
                    switchMap(
                        updatedAuditCampaign => {
                            if (!(updatedAuditCampaign instanceof Error)) {

                                if(newItemsIds && newItemsIds.length) {
                                    return this.applyAfterPushOperations(
                                        newItemsIds, 
                                        updatedAuditCampaign, 
                                        dataToUpdate
                                    )
                                }
                                return of({ success: true })
                            }
                            return of({ success: false })
                        }
                    ),
                    tap(result => {
                        if(result.success) {
                            this.offlineDataUpdateCacher.removeCachedValue(dataToUpdate.id)
                        }
                    }),
                )
        }
        else {
            return of({ success: false })
        }
    }

    private applyAfterPushOperations(newItems: string[], updatedAuditCampaign: AuditCampaign, dataToUpdate: SynchroMediaUpdates<AuditCampaign>): Observable<PushCachedValueResult> {
        // add offline created items to the campaign
        const itemDataStore = ItemDataStore.getInstance()

        let addItemPromises: Promise<any>[] = []

        for(const newItemCacheKey of newItems) {
            const newItemId = itemDataStore.getUpdatedValue(newItemCacheKey)

            if(!newItemId || newItemId === 'inProgress') {
                return of({ success: false })
            }

            addItemPromises.push(
                this.addItem(updatedAuditCampaign, newItemId, 'PhysicalMuseumItem', true)
            )
        }


        // @ts-ignore (allSettled) update ts
        return from(Promise.allSettled(addItemPromises)).pipe(
            switchMap(allResults => {
                // @ts-ignore
                if(allResults.every(result => result.status === "fulfilled")) {
                    return of({ success: true })
                }
                return of({ success: false })
            }),
        )
                    /*
                    map(() => itemDataStore.getUpdatedValue(item.linkableItem)),
                    tap(id=> {
                        if (!id || id === 'inProgress') {
                            throw new Error('not yet updated')
                        }
                    }),
                    retryWhen(errors => errors.pipe(delayWhen(() => timer(1000))))
                    subscribe addItem
                    */
    }

    private prepareDataToSyncUp(updatedCampaign: any): Partial<AuditCampaign> {
        let res: Partial<AuditCampaign> = {}

        if(updatedCampaign) {
            if(updatedCampaign.auditProjectSchema) {
                const auditProjectSchema = updatedCampaign.auditProjectSchema

                if(auditProjectSchema.auditReports) {
                    if(auditProjectSchema.auditReports.length) {
                        for(let i=0; i<auditProjectSchema.auditReports.length; i++) {
                            const auditReport = auditProjectSchema.auditReports[i]

                            // recolement ref
                            if(auditReport) {
                                let ref
                                if(auditReport.ref) {
                                    ref = auditReport.ref
                                } else {
                                    ref = auditReport
                                }
                                // the offline created recolements will be created automatically when we push 
                                // the new item, and we will update them later from RecordDataStore (soon to be renamed RecolementDataStore)
                                if(!OfflineDataFetcher.isTemporaryId(ref)) {
                                    _.set(
                                        res,
                                        `auditProjectSchema.auditReports[${i}]`,
                                        ref
                                    )
                                }
                            }
                        }
                    }
                }
                // else nothing, because we cannot suppress recolements from audit campaign in Audit (at least not in offline mode)
            }

            if(updatedCampaign.workflowSchema) {
                const workflowSchema = updatedCampaign.workflowSchema

                if(workflowSchema.linkableItems) {
                    if(workflowSchema.linkableItems.length) {
                        for(let i=0; i<workflowSchema.linkableItems.length; i++) {
                            const linkableItem = workflowSchema.linkableItems[i]

                            // item ref
                            if(linkableItem?.linkableItem) {
                                let ref
                                if(linkableItem.linkableItem.ref) {
                                    ref = linkableItem.linkableItem.ref 
                                } else if(linkableItem.linkableItem.linkableItem) {
                                    ref = linkableItem.linkableItem.linkableItem
                                }
                                // in this.pushCachedUpdate and this.applyAfterPushOperations, we keep track of
                                // newly offline created item, and we add them later
                                if(ref && !OfflineDataFetcher.isTemporaryId(ref)) {
                                    _.set(
                                        res,
                                        `workflowSchema.linkableItems[${i}].linkableItem.linkableItem`,
                                        ref
                                    )
                                } else {
                                    // we don't need to treat other linkableItem related fields for this index
                                    continue
                                }

                                let refType
                                if(linkableItem.linkableItem.referenceType) {
                                    refType = linkableItem.linkableItem.referenceType
                                } else if(linkableItem.linkableItem.linkableItemReferenceType) {
                                    refType = linkableItem.linkableItem.linkableItemReferenceType
                                }
                                _.set(
                                    res,
                                    `workflowSchema.linkableItems[${i}].linkableItem.linkableItemReferenceType`,
                                    refType
                                )
                            }
                        }
                    }
                    // else nothing, because we cannot suppress items from audit campaign in Audit (at least not in offline mode)
                }
            }
        }

        return res
    }

    public cleanRefs(obj) {
        if (obj && typeof obj === 'object') {
            if (obj.ref) {
                return obj.ref
            }
            let keys = Object.keys(obj)
            const clean = {}
            for (const field of keys) {
                if (Array.isArray(obj[field])) {
                    clean[field] = obj[field].map(e => this.cleanRefs(e))
                } else if (typeof obj[field] === 'object') {
                    clean[field] = this.cleanRefs(obj[field])
                } else {
                    clean[field] = obj[field]
                }
            }

            return clean
        }

        return obj
    }

    public fixLinkedItem(obj) {
        obj.recolementLinkSchema.linkedItem = { linkableItem: obj.recolementLinkSchema.linkedItem }
        return obj
    }

    public update(id: string, body: AuditCampaign, endpointOverride?: string, ignoreCachedStatus: boolean = false) {
        if (window.navigator.onLine) {
            // Saving records with local ids
            const auditRecordsBeforeUpdate = auditCampaignSelectors.auditRecords(body).sort();

            // Removing temporary record ids to avoid Error 500 on update
            if(body.auditProjectSchema?.auditReports) {
                body.auditProjectSchema.auditReports = auditCampaignSelectors.auditRecords(body).filter(recordId => {
                    return !OfflineDataFetcher.isTemporaryId(recordId)
                })
            }

            const linkableItemsBeforeUpdate = auditCampaignSelectors.linkableItems(body)

            if(body.workflowSchema?.linkableItems) {
                body.workflowSchema.linkableItems = auditCampaignSelectors.linkableItems(body).filter(linkableItem => {
                    return linkableItem.linkableItem?.linkableItem && 
                        !OfflineDataFetcher.isTemporaryId(linkableItem.linkableItem?.linkableItem)
                })
            }


            let needOfflineUpdate = true

            // Updating campaign to create missing records from linkable items
            return super.update(id, body, endpointOverride, ignoreCachedStatus).pipe(
                switchMap(response => this.fetchOne(id)),
                tap(
                    response => {
                        // Retieving records, including the new ones freshly created
                        const auditRecordsAfterUpdate = auditCampaignSelectors.auditRecords(response).sort();

                        // Getting the created records that are not in cache yet (? perfs with a lot of records ?)
                        const diff = auditRecordsAfterUpdate.filter(
                            recordId => !auditRecordsBeforeUpdate.includes(recordId)
                        )

                        // Replacing temp ids in local records and pushing them to db
                        Promise.all(auditRecordsBeforeUpdate
                            .filter(recordId => OfflineDataFetcher.isTemporaryId(recordId)) // keep only ids of locally created entities
                            .map(
                                async (recordId, index) => {
                                    const dbId = diff[index]
                                    const updateBody = await RecordDataStore.getInstance().getObjectInUpdateCache(recordId);
                                    if (dbId) {
                                        const existingBody = await RecordDataStore.getInstance().fetchOne(dbId).pipe(first()).toPromise();

                                        needOfflineUpdate = false 
                                        
                                        // Replacing  temporary id in body
                                        updateBody.id = dbId;

                                        // Keeping readonly fields to avoid error on update
                                        _.set(updateBody, 'recolementIdentificationSchema.recoleur', _.get(existingBody, 'recolementIdentificationSchema.recoleur'));
                                        _.set(updateBody, 'recolementIdentificationSchema.authorizedAuditor', _.get(existingBody, 'recolementIdentificationSchema.authorizedAuditor'));
                                        _.set(updateBody, 'institutionSharing', _.get(existingBody, 'institutionSharing'));

                                        RecordDataStore.getInstance().update(dbId, updateBody, undefined, ignoreCachedStatus).subscribe(
                                            saveResponse => {
                                                // Removing freshly saved record from cache
                                                RecordDataStore.getInstance().removeObjectFromUpdateCache(recordId);
                                            }
                                        );
                                    }
                                }
                            ))
                        if (needOfflineUpdate) {
                            if(linkableItemsBeforeUpdate) {
                                _.set(
                                    body,
                                    'workflowSchema.linkableItems',
                                    linkableItemsBeforeUpdate
                                )
                            }
                            if(auditRecordsBeforeUpdate) {
                                _.set(
                                    body,
                                    'auditProjectSchema.auditReports',
                                    auditRecordsBeforeUpdate
                                )
                            }
                            super.updateOfflineMode(id, body)
                        }
                    })
            );
        }
        return super.update(id, body, endpointOverride, ignoreCachedStatus);
    }

    public async addItem(
        auditCampaign: AuditCampaign,
        newItemId: string,
        itemType: string = DEFAULT_ITEM_TYPE,
        ignoreCacheStatus: boolean = false,
    ): Promise<any> {
        const linkableItems: ProjectLinkableItemStateResource[] = auditCampaignSelectors.linkableItems(auditCampaign);
        const newLinkedItem: ProjectLinkableItemStateResource = {
            linkableItem: {
                linkableItem: newItemId,
                linkableItemReferenceType: itemType,   
            },
        }
        linkableItems.push(newLinkedItem)

        let updatedAuditCampaign = _.cloneDeep(auditCampaign)

        _.set(
            updatedAuditCampaign,
            'workflowSchema.linkableItems',
            linkableItems
        )

        // Creating local record if offline mode or item is not online yet
        if (!window.navigator.onLine || OfflineDataFetcher.isTemporaryId(newItemId)) {
            const newRecord = await RecordDataStore.getInstance().createOfflineRecordForCampaign(
                newLinkedItem.linkableItem!.linkableItem!,
                newLinkedItem.linkableItem!.linkableItemReferenceType!,
                auditCampaign.id
            ).toPromise()

            if (newRecord) {
                let updatedAuditReports = _.get(
                    updatedAuditCampaign, 
                    'auditProjectSchema.auditReports',
                    []
                )
                updatedAuditReports.push(newRecord.id)

                _.set(
                    updatedAuditCampaign,
                    'auditProjectSchema.auditReports',
                    updatedAuditReports
                )
            }
        }

        return await this.update(auditCampaign.id, updatedAuditCampaign, undefined, ignoreCacheStatus)
            .pipe(first())
            .toPromise()
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new AuditCampaignOfflineDataFetcher(this.sanitizedIdentifer);
    }
}